import React, { useMemo } from 'react';
import { Lock } from 'lucide-react';
import { RecipeSection } from './RecipeSection';
import {
  findViewById,
  viewGroupsToRecipeGroups,
  type IView,
  type IViewGroup,
} from '../interfaces/IView';

interface ViewsGalleryProps {
  /** Grouped views to offer (grouped by concern). */
  groups: readonly IViewGroup[];
  /** The currently-applied view id, highlighted in the list. */
  activeViewId: string | null;
  /** Called when an entitled view is chosen. */
  onSelect: (view: IView) => void;
  /** Called when the active view is clicked and `allowDeselect` is on. */
  onClear?: () => void;
  /** Entitlement check; views with an `entitlementKey` that fails render locked. */
  isEntitled?: (entitlementKey: string) => boolean;
  /** Called when a locked view is clicked — surface the upgrade affordance. */
  onUpgrade?: (view: IView) => void;
  /** Picker surfaces always switch (default false); set true to allow clearing. */
  allowDeselect?: boolean;
  className?: string;
}

const LockBadge: React.FC = () => (
  <span className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[9px] font-bold uppercase text-muted-foreground">
    <Lock className="h-3 w-3" />
  </span>
);

/**
 * ViewsGallery — the single, shared "Change view" picker, generalized from
 * the Compass "Change review view" list (the gold standard). Grouped by
 * concern, outcome-named, with `Live`/data badges and visible-but-locked
 * gating for premium views. Reuses `RecipeSection` for the card aesthetic so
 * there is exactly one card renderer across the product.
 */
export const ViewsGallery: React.FC<ViewsGalleryProps> = ({
  groups,
  activeViewId,
  onSelect,
  onClear,
  isEntitled,
  onUpgrade,
  allowDeselect = false,
  className,
}) => {
  const isLocked = useMemo(() => {
    return (view: IView): boolean =>
      Boolean(view.entitlementKey) && isEntitled ? !isEntitled(view.entitlementKey as string) : false;
  }, [isEntitled]);

  const recipeGroups = useMemo(
    () =>
      viewGroupsToRecipeGroups(groups, (view) => (isLocked(view) ? <LockBadge /> : view.badge)),
    [groups, isLocked],
  );

  const handleSelect = (id: string | null) => {
    if (id === null) {
      onClear?.();
      return;
    }
    const view = findViewById(groups, id);
    if (!view) return;
    if (isLocked(view)) {
      onUpgrade?.(view);
      return;
    }
    onSelect(view);
  };

  return (
    <RecipeSection
      groups={recipeGroups}
      activeRecipeId={activeViewId}
      onSelect={handleSelect}
      allowDeselect={allowDeselect}
      className={className}
    />
  );
};
